<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="4">
          <FormulateInput
            id="period"
            v-model="filters.period"
            type="date-range-picker"
            :label="$t('Período')"
            :time-picker="false"
          />
        </b-col>

        <b-col md="4">
          <e-store-combo
            v-model="filters.storeIds"
            :multiple="true"
            :required="false"
            :select-single-option="false"
            :label="$t('Lojas')"
            :placeholder="$t('Todas')"
          />
        </b-col>

        <b-col md="4">
          <FormulateInput
            id="days-of-week"
            v-model="filters.daysOfWeek"
            type="vue-select"
            :multiple="true"
            :label="$t('Dias da semana')"
            :placeholder="$t('Todos')"
            :options="stockCountingDayOfWeekOptions()"
          />
        </b-col>

        <b-col md="3">
          <FormulateInput
            id="group-id"
            v-model="filters.groupId"
            type="vue-select"
            :label="$t('Grupo')"
            :placeholder="$t('Todos')"
            :options="getComboGroups"
          />
        </b-col>

        <b-col md="3">
          <FormulateInput
            id="brand-id"
            v-model="filters.brandId"
            type="vue-select"
            :label="$t('Marca')"
            :placeholder="$t('Todas')"
            :options="getComboBrands"
          />
        </b-col>

        <b-col md="2">
          <FormulateInput
            id="min-divergent-skus-percent"
            v-model="filters.minDivergentSkusPercent"
            type="text-number"
            :precision="2"
            :label="$t('% mín. SKUs divergentes')"
          />
        </b-col>

        <b-col md="2">
          <FormulateInput
            id="only-non-compliant"
            v-model="filters.onlyNonCompliant"
            type="switch"
            :label="$t('Somente não conformes')"
          />
        </b-col>

        <b-col md="2">
          <FormulateInput
            id="only-with-divergence"
            v-model="filters.onlyWithDivergence"
            type="switch"
            :label="$t('Somente com divergência')"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-row v-if="summary">
      <b-col
        v-for="card in summaryCards"
        :key="card.title"
        md="2"
        sm="6"
      >
        <statistic-card-horizontal
          :icon="card.icon"
          :color="card.color"
          :statistic="card.value"
          :statistic-title="card.title"
        />
      </b-col>
    </b-row>

    <b-alert
      v-if="summary && summary.evaluatedUntil"
      show
      variant="light"
      class="p-1"
    >
      <small>
        {{
          $t('Aderência avaliada até {date}. O dia corrente não entra no cálculo porque as lojas ainda podem realizar a contagem.', {
            date: $options.filters.date(summary.evaluatedUntil),
          })
        }}
      </small>
    </b-alert>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="activePaging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-tabs v-model="activeTab">
        <b-tab :title="$t('Aderência às regras')">
          <stock-counting-compliance-table
            class="mt-1"
            :items="compliance"
            :paging="compliancePaging"
            :query-executed="queryExecuted"
            @page-change="compliancePageChange"
          />
        </b-tab>

        <b-tab :title="$t('Divergências')">
          <stock-counting-divergence-table
            class="mt-1"
            :items="divergences"
            :paging="divergencePaging"
            :query-executed="queryExecuted"
            @page-change="divergencePageChange"
            @read-only="showCounting"
          />
        </b-tab>
      </b-tabs>
    </b-card-actions>
  </div>
</template>

<script>
import { BRow, BCol, BTabs, BTab, BAlert } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import { EFilters, EStoreCombo, EPageSizeSelector } from '@/views/components'
import { formulateValidation, productDomains, stockDomain } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import StockCountingComplianceTable from './components/StockCountingComplianceTable.vue'
import StockCountingDivergenceTable from './components/StockCountingDivergenceTable.vue'

const COMPLIANCE_TAB = 0

export default {
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    BAlert,
    BCardActions,
    StatisticCardHorizontal,
    EFilters,
    EStoreCombo,
    EPageSizeSelector,
    StockCountingComplianceTable,
    StockCountingDivergenceTable,
  },

  mixins: [formulateValidation, stockDomain, productDomains],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
      activeTab: COMPLIANCE_TAB,
    }
  },

  computed: {
    ...mapGetters('pages/catalog/products', ['getComboGroups', 'getComboBrands']),
    ...mapState('pages/stock/stockCountingDashboard', {
      summary: 'summary',
      compliance: 'compliance',
      compliancePaging: 'compliancePaging',
      divergences: 'divergences',
      divergencePaging: 'divergencePaging',
      filters: 'filters',
    }),

    /** O seletor de itens por página no cabeçalho do card age sobre a aba visível. */
    activePaging() {
      return this.activeTab === COMPLIANCE_TAB ? this.compliancePaging : this.divergencePaging
    },

    summaryCards() {
      const { summary } = this
      const percent = (value, decimals = 1) => this.$options.filters.percentage(value, decimals)

      return [
        {
          title: this.$t('Aderência às regras'),
          value: percent(summary.compliancePercent),
          icon: 'CheckCircleIcon',
          color: this.complianceColor(summary.compliancePercent),
        },
        {
          title: this.$t('Dias não cumpridos'),
          value: this.$options.filters.number(summary.missedDays, 0),
          icon: 'CalendarIcon',
          color: summary.missedDays > 0 ? 'danger' : 'success',
        },
        {
          title: this.$t('Lojas não conformes'),
          value: `${summary.nonCompliantStores} / ${summary.storesEvaluated}`,
          icon: 'HomeIcon',
          color: summary.nonCompliantStores > 0 ? 'danger' : 'success',
        },
        {
          title: this.$t('Contagens finalizadas'),
          value: this.$options.filters.number(summary.completedCountings, 0),
          icon: 'ClipboardIcon',
          color: 'primary',
        },
        {
          title: this.$t('Contagens c/ divergência'),
          value: `${summary.countingsWithDivergence} (${percent(
            summary.countingsWithDivergencePercent,
            0
          )})`,
          icon: 'AlertTriangleIcon',
          color: summary.countingsWithDivergence > 0 ? 'warning' : 'success',
        },
        {
          title: this.$t('SKUs acima do limite'),
          value: `${summary.divergentSkus} (${percent(summary.divergentSkusPercent, 0)})`,
          icon: 'PackageIcon',
          color: summary.divergentSkus > 0 ? 'warning' : 'success',
        },
      ]
    },
  },

  async mounted() {
    try {
      this.fetching = true
      await this.fetchProductCombos()
    } catch (error) {
      this.showError({ error })
    } finally {
      this.fetching = false
    }

    await this.getData()
  },

  methods: {
    ...mapActions('pages/stock/stockCountingDashboard', [
      'fetchDashboard',
      'fetchCompliance',
      'fetchDivergences',
      'resetFilters',
      'setComplianceCurrentPage',
      'setCompliancePageSize',
      'setDivergenceCurrentPage',
      'setDivergencePageSize',
    ]),
    ...mapActions('pages/catalog/products', ['fetchProductCombos']),

    complianceColor(value) {
      if (value >= 1) return 'success'
      if (value >= 0.8) return 'warning'
      return 'danger'
    },

    async getData() {
      try {
        this.fetching = true
        await this.fetchDashboard()
        this.queryExecuted = true
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    async reloadGrid(action) {
      try {
        this.fetching = true
        await action()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    filterData() {
      this.setComplianceCurrentPage(1)
      this.setDivergenceCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      if (this.activeTab === COMPLIANCE_TAB) {
        this.setCompliancePageSize(pageSize)
        this.reloadGrid(this.fetchCompliance)
      } else {
        this.setDivergencePageSize(pageSize)
        this.reloadGrid(this.fetchDivergences)
      }
    },

    compliancePageChange(currentPage) {
      this.setComplianceCurrentPage(currentPage)
      this.reloadGrid(this.fetchCompliance)
    },

    divergencePageChange(currentPage) {
      this.setDivergenceCurrentPage(currentPage)
      this.reloadGrid(this.fetchDivergences)
    },

    showCounting(row) {
      this.$router.push({
        name: 'stock-counting-read-only',
        params: { id: row.countingId },
      })
    },
  },
}
</script>

<style lang="scss" scoped></style>
